import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";

import { ColumnMode } from "@swimlane/ngx-datatable";
import { BaseV2Component } from "app/base-module/base-component-v2/base-component-v2.component";
import { BaseV2Params } from "app/base-module/base-component-v2/base-component-v2.types";
import { AuthService } from "app/base-module/base-service/auth.service";
import {
    BoletaDeIntermediacao,
    BoletaDeIntermediacaoQueryParams,
    FiltrosBoletaDeIntermediacao,
} from "app/boleta-de-intermediacao/models/boleta-de-intermediacao.model";
import { BOLETA_STATUS_LABELS, BoletaStatus } from "app/boleta-de-intermediacao/models/boleta-status.enum";
import { BoletaDeIntermediacaoService } from "app/boleta-de-intermediacao/services/boleta-de-intermediacao.service";
import { ApiPaginationResponseDataNonNullable } from "app/shared/models/api-response.model";
import { BoletaDeIntermediacaoPermissao } from "app/shared/permissoes-enum";
import { Usuario } from "app/usuarios/models/user.model";
import { ErrorApiUtils } from "src/utils/error-api-utils";

export interface BoletasWidgetParams extends BaseV2Params, FiltrosBoletaDeIntermediacao {}

@Component({
    selector: "boletas-widget",
    templateUrl: "./boletas-widget.component.html",
    styleUrls: ["./boletas-widget.component.scss"],
})
export class BoletasWidgetComponent
    extends BaseV2Component<BoletaDeIntermediacao, BoletasWidgetParams>
    implements OnInit
{
    user: Usuario | null = null;
    ColumnMode = ColumnMode;

    // Permissões
    hasPermission = false;
    defaultQueryParams: BoletaDeIntermediacaoQueryParams = {
        page: 1,
        per_page: 5,
        sortColumn: "created_at",
        sortOrder: "desc",
        hideUnusedStatus: true,
    };

    // Labels para status
    statusLabels = BOLETA_STATUS_LABELS;

    constructor(
        public dialog: MatDialog,
        override snackBar: MatSnackBar,
        private _authService: AuthService,
        public _router: Router,
        private _boletaService: BoletaDeIntermediacaoService
    ) {
        super(dialog, snackBar);
        const strUser = this._authService.getUser();
        this.user = strUser ? JSON.parse(strUser) : null;

        // Configurar parâmetros para BaseV2Component
        this.params = {
            ...this.defaultQueryParams,
        } as BoletasWidgetParams;
    }

    ngOnInit(): void {
        this.checkPermissions();

        if (this.hasPermission) {
            this.getAll();
        }
    }

    checkPermissions(): void {
        this.hasPermission = this._authService.isPermissao([BoletaDeIntermediacaoPermissao.LISTAR]);
    }

    /**
     * Carrega as boletas de intermediação usando BaseV2Component
     */
    getAll(): void {
        this.loadingIndicator = true;

        this._boletaService.buscarBoletas(this.params).subscribe({
            next: (response: ApiPaginationResponseDataNonNullable<BoletaDeIntermediacao>) => {
                this.length = response.meta?.total ?? 0;
                this.rows = response.data;

                this.loadingIndicator = false;
            },
            error: (error) => {
                const errorMessage = ErrorApiUtils.extractErrorMessage(
                    error,
                    "Erro ao carregar boletas de intermediação. Tente novamente."
                );
                this.snackBar.open(errorMessage, "Fechar", {
                    duration: 3000,
                    verticalPosition: "top",
                    horizontalPosition: "center",
                    panelClass: "snackbar-error",
                });
                this.loadingIndicator = false;
            },
        });
    }

    /**
     * Retorna a descrição do ativo da boleta de intermediação
     * @param boleta Boleta de intermediação
     * @returns string
     */
    displayFnDescricaoAtivo(boleta: BoletaDeIntermediacao): string {
        const formatString = (value?: string | null): string => value || "N/A";
        return (
            formatString(boleta.cliente_vendedor?.ativo?.descricao) +
            " - " +
            formatString(boleta.cliente_vendedor?.ativo?.emissor)
        );
    }

    /**
     * Redireciona para a lista completa de boletas
     */
    redirectToFullList(): void {
        this._router.navigate(["/boleta-de-intermediacao/lista"]);
    }

    /**
     * Manipula eventos de paginação usando BaseV2Component
     */
    onPageEvent(event: PageEvent): void {
        this.params.page = event.pageIndex + 1;
        this.params.per_page = event.pageSize;
        this.getAll();
    }

    /**
     * Retorna o label do status formatado
     */
    getStatusLabel(status: BoletaStatus): string {
        return this.statusLabels[status] || status;
    }
}
